import React, { useState, useEffect } from 'react';
import config from '../../config';
import './StockLevels.css';

interface InventoryItem {
  id: string;
  lotNumber?: string;
  expirationDate?: string;
  quantityOnHand: number;
  quantityReserved: number;
  unitCost: number;
  totalCost: number;
  status: string;
  location?: string;
  supplierItem: {
    id: string;
    supplierSku: string;
    packSize: number;
    packUnit: string;
    item: {
      id: string;
      name: string;
      category: string;
      baseUnit: string;
    };
    supplier: {
      id: string;
      name: string;
    };
  };
}

interface StockLevelsProps {
  selectedStore: string;
  enterpriseId: string;
}

const StockLevels: React.FC<StockLevelsProps> = ({ selectedStore, enterpriseId }) => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'category' | 'quantity' | 'expiration'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (selectedStore) {
      fetchInventory();
    }
  }, [selectedStore]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/inventory/stock/${selectedStore}`);
      
      if (response.ok) {
        const data = await response.json();
        // Ensure we're setting an array - API response may have data in a 'data' property
        setInventory(Array.isArray(data) ? data : (data.data || []));
      } else {
        console.error('Failed to fetch inventory');
        setInventory([]);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setInventory([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategories = () => {
    const inventoryArray = Array.isArray(inventory) ? inventory : [];
    const categorySet = new Set(inventoryArray.map(item => item.supplierItem.item.category));
    const categories = Array.from(categorySet);
    return categories.sort();
  };

  const getStatusOptions = () => {
    const inventoryArray = Array.isArray(inventory) ? inventory : [];
    const statusSet = new Set(inventoryArray.map(item => item.status));
    const statuses = Array.from(statusSet);
    return statuses.sort();
  };

  const filteredAndSortedInventory = () => {
    // Ensure inventory is an array before filtering
    const inventoryArray = Array.isArray(inventory) ? inventory : [];
    let filtered = inventoryArray.filter(item => {
      const matchesSearch = item.supplierItem.item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.supplierItem.supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.supplierItem.supplierSku && item.supplierItem.supplierSku.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = !categoryFilter || item.supplierItem.item.category === categoryFilter;
      const matchesStatus = !statusFilter || item.status === statusFilter;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.supplierItem.item.name;
          bValue = b.supplierItem.item.name;
          break;
        case 'category':
          aValue = a.supplierItem.item.category;
          bValue = b.supplierItem.item.category;
          break;
        case 'quantity':
          aValue = a.quantityOnHand;
          bValue = b.quantityOnHand;
          break;
        case 'expiration':
          aValue = a.expirationDate ? new Date(a.expirationDate) : new Date('9999-12-31');
          bValue = b.expirationDate ? new Date(b.expirationDate) : new Date('9999-12-31');
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const getDaysUntilExpiration = (expirationDate?: string) => {
    if (!expirationDate) return null;
    const today = new Date();
    const expiry = new Date(expirationDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpirationStatus = (expirationDate?: string) => {
    if (!expirationDate) return '';
    
    const days = getDaysUntilExpiration(expirationDate);
    if (days === null) return '';
    
    if (days < 0) return 'expired';
    if (days <= 3) return 'critical';
    if (days <= 7) return 'warning';
    return 'normal';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="stock-loading">
        <div className="loading-spinner"></div>
        <p>Loading inventory...</p>
      </div>
    );
  }

  const displayedInventory = filteredAndSortedInventory();
  const totalValue = displayedInventory.reduce((sum, item) => sum + item.totalCost, 0);

  return (
    <div className="stock-levels">
      {/* Header and Summary */}
      <div className="stock-header">
        <div className="header-info">
          <h2>Current Stock Levels</h2>
          <div className="stock-summary">
            <div className="summary-item">
              <span className="summary-label">Total Items:</span>
              <span className="summary-value">{displayedInventory.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Value:</span>
              <span className="summary-value">{formatCurrency(totalValue)}</span>
            </div>
          </div>
        </div>
        <button className="refresh-btn" onClick={fetchInventory}>
          🔄 Refresh
        </button>
      </div>

      {/* Filters and Search */}
      <div className="stock-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search items, suppliers, or SKUs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {getCategories().map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            {getStatusOptions().map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field as any);
              setSortOrder(order as any);
            }}
            className="filter-select"
          >
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="category-asc">Category A-Z</option>
            <option value="quantity-desc">Quantity High-Low</option>
            <option value="quantity-asc">Quantity Low-High</option>
            <option value="expiration-asc">Expiring Soon</option>
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      {displayedInventory.length === 0 ? (
        <div className="empty-inventory">
          <h3>No Inventory Found</h3>
          <p>No items match your current filters or this store has no inventory.</p>
        </div>
      ) : (
        <div className="inventory-table-container">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th>Supplier</th>
                <th>Quantity</th>
                <th>Location</th>
                <th>Expiration</th>
                <th>Unit Cost</th>
                <th>Total Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {displayedInventory.map((item) => (
                <tr key={item.id} className={`inventory-row ${getExpirationStatus(item.expirationDate)}`}>
                  <td>
                    <div className="item-info">
                      <div className="item-name">{item.supplierItem.item.name}</div>
                      <div className="item-sku">SKU: {item.supplierItem.supplierSku}</div>
                      {item.lotNumber && (
                        <div className="item-lot">Lot: {item.lotNumber}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">
                      {item.supplierItem.item.category}
                    </span>
                  </td>
                  <td>{item.supplierItem.supplier.name}</td>
                  <td>
                    <div className="quantity-info">
                      <div className="quantity-main">
                        {item.quantityOnHand.toFixed(2)} {item.supplierItem.item.baseUnit}
                      </div>
                      {item.quantityReserved > 0 && (
                        <div className="quantity-reserved">
                          ({item.quantityReserved.toFixed(2)} reserved)
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{item.location || 'Not specified'}</td>
                  <td>
                    <div className={`expiration-info ${getExpirationStatus(item.expirationDate)}`}>
                      {item.expirationDate ? (
                        <>
                          <div>{formatDate(item.expirationDate)}</div>
                          {getDaysUntilExpiration(item.expirationDate) !== null && (
                            <div className="days-until">
                              {getDaysUntilExpiration(item.expirationDate)! < 0 
                                ? `${Math.abs(getDaysUntilExpiration(item.expirationDate)!)} days overdue`
                                : `${getDaysUntilExpiration(item.expirationDate)} days left`
                              }
                            </div>
                          )}
                        </>
                      ) : (
                        'No expiration'
                      )}
                    </div>
                  </td>
                  <td>{formatCurrency(item.unitCost)}</td>
                  <td>{formatCurrency(item.totalCost)}</td>
                  <td>
                    <span className={`status-badge ${item.status}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StockLevels;